class CstmCollectionList extends HTMLElement {
  constructor() {
    super();
    this.listContent = this.querySelector('#collection-list-content');
    this.loadMoreBtn = this.querySelector('.js-load-more-collections');
    this.collectionsPerPage = parseInt(this.listContent?.getAttribute('data-collections-per-page')) || 3;

    // Initialize virtual state from current URL
    this.currentSearchParams = new URLSearchParams(window.location.search);

    this.handleGlobalClick = this.handleGlobalClick.bind(this);
  }

  connectedCallback() {
    this.initCollectionLoadMore();
    this.initFilterInterception();
    this.addEventListener('click', this.handleGlobalClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleGlobalClick, true);
  }

  handleGlobalClick(e) {
    const clearBtn = e.target.closest('.facets__clear-all');
    const clearComponent = e.target.closest('facet-remove-component');

    if (clearBtn || clearComponent) {
      // Determine context: Drawer or Desktop
      const drawer = (clearBtn || clearComponent).closest('#filters-drawer') || (clearBtn || clearComponent).closest('dialog-component');

      let formComponent;
      if (drawer) {
        formComponent = drawer.querySelector('facets-form-component');
      } else {
        formComponent = (clearBtn || clearComponent).closest('facets-form-component') || this.querySelector('facets-form-component');
      }

      if (formComponent) {
        this.handleClearAllClick(e, formComponent);
      }
    }
  }

  initCollectionLoadMore() {
    if (!this.listContent) return;

    this.ensureVisibleCount(this.collectionsPerPage);

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentCount = this.querySelectorAll('.cstm-collection-row').length;
        const targetCount = currentCount + this.collectionsPerPage;
        this.ensureVisibleCount(targetCount);
      });
    }
  }

  ensureVisibleCount(targetCount) {
    const currentRows = this.querySelectorAll('.cstm-collection-row');
    const currentCount = currentRows.length;

    if (currentCount >= targetCount) {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.classList.remove('loading');
        this.loadMoreBtn.textContent = 'Load More';
      }
      return;
    }

    const nextUrl = this.loadMoreBtn ? this.loadMoreBtn.getAttribute('href') : null;
    if (!nextUrl) {
      if (this.loadMoreBtn) this.loadMoreBtn.remove();
      return;
    }

    if (this.loadMoreBtn && !this.loadMoreBtn.classList.contains('loading')) {
      this.loadMoreBtn.classList.add('loading');
      this.loadMoreBtn.textContent = 'Loading...';
    }

    fetch(nextUrl)
      .then((response) => response.text())
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newRows = doc.querySelectorAll('.cstm-collection-row');
        const nextBtn = doc.querySelector('.js-load-more-collections');
        const loadMoreContainer = this.querySelector('.cstm-load-more-container');

        if (loadMoreContainer) {
          newRows.forEach((row) => {
            loadMoreContainer.before(row);
          });
        }

        if (this.loadMoreBtn) {
          if (nextBtn) {
            this.loadMoreBtn.setAttribute('href', nextBtn.getAttribute('href'));
            this.ensureVisibleCount(targetCount);
          } else {
            this.loadMoreBtn.removeAttribute('href');
            this.loadMoreBtn.remove();
          }
        }
      })
      .catch((err) => {
        console.error('Error loading collections:', err);
        if (this.loadMoreBtn) {
          this.loadMoreBtn.classList.remove('loading');
          this.loadMoreBtn.textContent = 'Load More';
        }
      });
  }

  initFilterInterception() {
    customElements.whenDefined('facets-form-component').then(() => {
      const facetsForms = this.querySelectorAll('facets-form-component');

      facetsForms.forEach((formComponent) => {
        formComponent.isClearing = false;

        formComponent.fetchProducts = (searchParams) => {
          // Update internal virtual state
          this.currentSearchParams = new URLSearchParams(searchParams);

          const originalPushState = history.pushState;
          history.pushState = function () { };

          const collectionPath = this.dataset.collectionUrl || '/collections/all';
          const targetUrl = new URL(collectionPath, window.location.origin);

          this.currentSearchParams.forEach((value, key) => {
            targetUrl.searchParams.append(key, value);
          });

          const setLoading = (isLoading) => {
            if (this.listContent) {
              if (isLoading) this.listContent.classList.add('loading');
              else this.listContent.classList.remove('loading');
            }
          };

          setLoading(true);

          fetch(targetUrl.toString())
            .then((response) => response.text())
            .then((text) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(text, 'text/html');

              let newContent = doc.getElementById('product-grid');
              if (!newContent) newContent = doc.querySelector('.product-grid');
              if (!newContent) newContent = doc.getElementById('ProductGridContainer');

              if (newContent && this.listContent) {
                this.listContent.innerHTML = '';
                this.listContent.appendChild(newContent);

                if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';

                this.handleProductPagination(doc, targetUrl);
                this.updateFacetCounts(doc);
              }

              history.pushState = originalPushState;
            })
            .catch((err) => {
              console.error('Error fetching filtered products:', err);
              history.pushState = originalPushState;
            })
            .finally(() => {
              setLoading(false);
            });
        };

        formComponent.updateFilters = (event) => {
          if (formComponent.isClearing) return;

          const form = formComponent.querySelector('form');
          const formData = new FormData(form);
          const searchParams = new URLSearchParams(formData);

          const sortBy = searchParams.get('sort_by');
          if (sortBy === 'in_stock') {
            searchParams.delete('sort_by');
            if (searchParams.get('filter.p.m.filter.ready_to_ship') !== '1') {
              searchParams.set('filter.p.m.filter.ready_to_ship', '1');
            }
          }

          if (!searchParams.has('sort_by') && this.currentSearchParams.has('sort_by')) {
            searchParams.set('sort_by', this.currentSearchParams.get('sort_by'));
          }

          const hasInStockFilter = searchParams.get('filter.p.m.filter.ready_to_ship') === '1';
          const hadInStockFilter = this.currentSearchParams.get('filter.p.m.filter.ready_to_ship') === '1';

          if (hasInStockFilter !== hadInStockFilter) {
            const newUrl = new URL(window.location.pathname, window.location.origin);
            searchParams.forEach((value, key) => {
              newUrl.searchParams.append(key, value);
            });
            window.location.href = newUrl.toString();
            return;
          }

          formComponent.fetchProducts(searchParams);
        };

        formComponent.updateFiltersByURL = (url) => {
          if (formComponent.isClearing) return;

          try {
            const urlObj = new URL(url, window.location.origin);
            const newParams = new URLSearchParams(urlObj.search);

            const hasInStockFilter = newParams.get('filter.p.m.filter.ready_to_ship') === '1';
            const hadInStockFilter = this.currentSearchParams.get('filter.p.m.filter.ready_to_ship') === '1';

            if (hasInStockFilter !== hadInStockFilter) {
              const newUrl = new URL(window.location.pathname, window.location.origin);
              newParams.forEach((value, key) => {
                newUrl.searchParams.append(key, value);
              });
              window.location.href = newUrl.toString();
              return;
            }

            formComponent.fetchProducts(newParams);
          } catch (e) {
            console.error('Error in updateFiltersByURL:', e);
          }
        };
      });
    });
  }

  handleClearAllClick(e, formComponent) {
    e.preventDefault();
    e.stopPropagation();

    formComponent.isClearing = true;

    const form = formComponent.querySelector('form');
    if (form) {
      // 1. Reset Checkboxes
      const checkedInputs = form.querySelectorAll('input[type="checkbox"]:checked');
      checkedInputs.forEach(input => {
        input.checked = false;
        input.removeAttribute('checked');
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Radio buttons
      const checkedRadios = form.querySelectorAll('input[type="radio"]:checked');
      checkedRadios.forEach(input => {
        if (input.name !== 'sort_by') {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // 2. Reset Price Range Slider
      document.dispatchEvent(new CustomEvent('theme:filters:clear', { bubbles: true }));

      // Fallback
      const priceSliders = form.querySelectorAll('price-range-slider');
      priceSliders.forEach(slider => {
        if (typeof slider.clearStoredState === 'function') {
          slider.clearStoredState();
        } else {
          const wrapper = slider.closest('[data-range-holder]');
          if (wrapper) {
            const minInput = wrapper.querySelector('input[name*=".gte"]');
            const maxInput = wrapper.querySelector('input[name*=".lte"]');
            const min = parseFloat(slider.getAttribute('min')) || 0;
            const max = parseFloat(slider.getAttribute('max')) || 100;

            if (minInput) minInput.value = min;
            if (maxInput) maxInput.value = max;

            slider.setAttribute('min-value', min);
            slider.setAttribute('max-value', max);
          }
        }
      });
    }

    formComponent.isClearing = false;

    // 3. Fetch Products - Keep only sorting
    const searchParams = new URLSearchParams();
    if (this.currentSearchParams.has('sort_by')) {
      searchParams.set('sort_by', this.currentSearchParams.get('sort_by'));
    } else if (form) {
      const sortInput = form.querySelector('input[name="sort_by"]:checked');
      if (sortInput) {
        searchParams.set('sort_by', sortInput.value);
      }
    }

    formComponent.fetchProducts(searchParams);
  }

  updateFacetCounts(doc) {
    const newTrigger = doc.querySelector('.facets__drawer-trigger');
    const currentTrigger = document.querySelector('.facets__drawer-trigger');
    if (newTrigger && currentTrigger) {
      const newCount = newTrigger.querySelector('.facets__drawer-trigger-count');
      if (newCount) {
        const currentCount = currentTrigger.querySelector('.facets__drawer-trigger-count');
        if (currentCount) currentCount.innerHTML = newCount.innerHTML;
      } else {
        currentTrigger.innerHTML = newTrigger.innerHTML;
      }
    }

    const newClearAll = doc.querySelector('.facets__clear-all');
    if (newClearAll) {
      const allCurrentClearAlls = document.querySelectorAll('.facets__clear-all');
      allCurrentClearAlls.forEach(btn => {
        btn.innerHTML = newClearAll.innerHTML;
        if (newClearAll.style.display === 'none' || newClearAll.classList.contains('hidden')) {
          btn.style.display = 'none';
          btn.closest('facet-remove-component')?.classList.add('hidden');
        } else {
          btn.style.display = '';
          btn.closest('facet-remove-component')?.classList.remove('hidden');
        }
      });
    }
  }

  handleProductPagination(doc, targetUrl) {
    let nextUrl = null;

    const paginationNext = doc.querySelector('.pagination__next');
    if (paginationNext) nextUrl = paginationNext.href;

    if (!nextUrl) {
      const nextLink = doc.querySelector('[rel="next"]');
      if (nextLink) nextUrl = nextLink.href;
    }

    if (!nextUrl) {
      const responseLoadMore = doc.querySelector('.product-grid__load-more-container');
      if (responseLoadMore) {
        const url = new URL(targetUrl);
        url.searchParams.set('page', 2);
        nextUrl = url.toString();
      }
    }

    let btnContainer = this.querySelector('#cstm-product-load-more-container');
    if (btnContainer) btnContainer.remove();

    const responseLoadMoreContainer = doc.querySelector('.product-grid__load-more-container');

    if (responseLoadMoreContainer && nextUrl) {
      btnContainer = responseLoadMoreContainer.cloneNode(true);
      btnContainer.id = 'cstm-product-load-more-container';

      const btn = btnContainer.querySelector('button, a');
      if (btn) {
        this.attachLoadMoreListener(btn, nextUrl, btnContainer);
      }

      this.listContent.appendChild(btnContainer);
    } else if (nextUrl) {
      btnContainer = document.createElement('div');
      btnContainer.id = 'cstm-product-load-more-container';
      btnContainer.className = 'product-grid__load-more-container';
      btnContainer.style.marginTop = '60px';
      btnContainer.style.display = 'flex';
      btnContainer.style.justifyContent = 'center';

      const productsLoadMoreBtn = document.createElement('a');
      productsLoadMoreBtn.id = 'cstm-product-load-more';
      productsLoadMoreBtn.className = 'button button--secondary';
      productsLoadMoreBtn.textContent = 'Load More';
      productsLoadMoreBtn.href = nextUrl;

      this.attachLoadMoreListener(productsLoadMoreBtn, nextUrl, btnContainer);

      btnContainer.appendChild(productsLoadMoreBtn);
      this.listContent.appendChild(btnContainer);
    }
  }

  attachLoadMoreListener(btn, initialUrl, btnContainer) {
    btn.dataset.nextUrl = initialUrl;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const urlToFetch = btn.dataset.nextUrl;
      if (!urlToFetch) return;

      btn.classList.add('loading');
      const originalText = btn.textContent;
      btn.textContent = 'Loading...';

      fetch(urlToFetch)
        .then(res => res.text())
        .then(resText => {
          const resDoc = new DOMParser().parseFromString(resText, 'text/html');
          let newItems = resDoc.querySelectorAll('.product-grid .product-grid__item, #product-grid .grid__item, #product-grid .product-grid__item, .product-grid li');

          const grid = this.listContent.querySelector('.product-grid') || this.listContent.querySelector('#product-grid');

          if (grid && newItems.length > 0) {
            Array.from(newItems).forEach(item => grid.appendChild(item));
          }

          let nextNextUrl = null;
          const nextPaginationNext = resDoc.querySelector('.pagination__next');
          if (nextPaginationNext) nextNextUrl = nextPaginationNext.href;
          else {
            const nextNextLink = resDoc.querySelector('[rel="next"]');
            if (nextNextLink) nextNextUrl = nextNextLink.href;
          }

          if (!nextNextUrl) {
            const urlObj = new URL(urlToFetch);
            const currentPage = parseInt(urlObj.searchParams.get('page')) || 1;
            const resGrid = resDoc.querySelector('.product-grid');
            const lastTotalPage = parseInt(resGrid?.getAttribute('data-last-page')) || 1;

            if (currentPage < lastTotalPage) {
              urlObj.searchParams.set('page', currentPage + 1);
              nextNextUrl = urlObj.toString();
            }
          }

          if (nextNextUrl) {
            btn.dataset.nextUrl = nextNextUrl;
            if (btn.tagName === 'A') btn.href = nextNextUrl;
            btn.classList.remove('loading');
            btn.textContent = originalText;
          } else {
            btnContainer.remove();
          }
        })
        .catch(err => {
          console.error('Error loading more products:', err);
          btn.classList.remove('loading');
          btn.textContent = originalText;
        });
    });
  }
}

customElements.define('cstm-collection-list', CstmCollectionList);
